import React, { useState } from 'react';
import { Settings, Cookie, Shield, BarChart3, Target, Eye, Save, X } from '../lib/icons';

const CookieSettingsButton: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    // Load current preferences from localStorage
    try {
      const saved = localStorage.getItem('cookieConsent');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          necessary: true,
          analytics: parsed.analytics ?? true,
          marketing: parsed.marketing ?? true,
          functional: parsed.functional ?? true
        };
      }
    } catch (e) {
      console.warn('Error loading cookie preferences:', e);
    }
    
    return {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
  });

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const savePreferences = () => {
    const prefsToSave = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(prefsToSave));
    setShowModal(false);
    
    // Simulate analytics event (but don't actually block anything)
    console.log('🍪 Cookie preferences updated:', prefsToSave);
    
    // Show success message
    const successDiv = document.createElement('div');
    successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    successDiv.textContent = 'Cookie-Einstellungen gespeichert';
    document.body.appendChild(successDiv);
    
    setTimeout(() => {
      document.body.removeChild(successDiv);
    }, 3000);
  };

  // Only show if user has already made a choice
  const hasConsent = localStorage.getItem('cookieConsent');
  if (!hasConsent) return null;

  return (
    <>
      {/* Floating Cookie Settings Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-4 left-4 w-12 h-12 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 transition-all duration-200 flex items-center justify-center z-40 hover:scale-110"
        title="Cookie-Einstellungen"
        aria-label="Cookie-Einstellungen öffnen"
      >
        <Cookie className="w-6 h-6" />
      </button>

      {/* Settings Modal */}
      {showModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={() => setShowModal(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Settings className="w-6 h-6 mr-3 text-blue-600" />
                    Cookie-Einstellungen verwalten
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <p className="text-gray-600 mb-8 leading-relaxed">
                  Verwalten Sie Ihre Cookie-Präferenzen. Änderungen werden sofort wirksam.
                </p>

                <div className="space-y-6">
                  {/* Necessary Cookies */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Shield className="w-6 h-6 text-green-600 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Notwendige Cookies</h3>
                          <p className="text-sm text-gray-600">Erforderlich für die Grundfunktionen</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-500 mr-3">Immer aktiv</span>
                        <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                          <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich und können nicht deaktiviert werden.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <BarChart3 className="w-6 h-6 text-blue-600 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Analyse-Cookies</h3>
                          <p className="text-sm text-gray-600">Google Analytics, Nutzungsstatistiken</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.analytics ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                      </button>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem sie anonyme Informationen sammeln und melden.
                    </p>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Target className="w-6 h-6 text-purple-600 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Marketing-Cookies</h3>
                          <p className="text-sm text-gray-600">Personalisierte Werbung und Inhalte</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('marketing')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.marketing ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                      </button>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Werden verwendet, um Besuchern relevante Anzeigen und Marketing-Kampagnen bereitzustellen.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <Eye className="w-6 h-6 text-orange-600 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Funktionale Cookies</h3>
                          <p className="text-sm text-gray-600">Erweiterte Website-Funktionen</p>
                        </div>
                      </div>
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                          preferences.functional ? 'bg-orange-500 justify-end' : 'bg-gray-300 justify-start'
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                      </button>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Ermöglichen erweiterte Funktionen und Personalisierung, wie z.B. die Speicherung Ihrer Präferenzen.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={savePreferences}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    Einstellungen speichern
                  </button>
                </div>

                {/* Legal Links */}
                <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                  <div className="flex flex-wrap justify-center gap-4 text-sm">
                    <a 
                      href="/datenschutz" 
                      className="text-blue-600 hover:text-blue-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Datenschutzerklärung
                    </a>
                    <a 
                      href="/impressum" 
                      className="text-blue-600 hover:text-blue-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Impressum
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CookieSettingsButton;